import { createContext, createElement, ReactNode, useContext, useEffect, useMemo } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { usePlatform } from "../platform/PlatformProvider";
import type { Platform } from "../platform/Platform";

import { isInIframe, isIPadUserAgent } from "../utils/browserUtils";

const STORAGE_KEY = "tabletShell";
const COARSE_POINTER_QUERY = "(pointer: coarse)";
const MIN_TABLET_WIDTH_QUERY = "(min-width: 744px)";

type ShellPreference = "on" | "off";

function parseUrlShellParam(): ShellPreference | null {
    const shell = new URLSearchParams(window.location.search).get("shell");
    if (shell === "tablet") return "on";
    if (shell === "desktop") return "off";
    return null;
}

function readStoredShellPreference(): ShellPreference | null {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return value === "on" || value === "off" ? value : null;
    } catch {
        return null;
    }
}

function persistShellPreference(value: ShellPreference): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
        // localStorage may be unavailable (private browsing, quota exceeded, iframe restrictions); ignore.
    }
}

/**
 * Persists the "desktop" preference and reloads. An auto-detected tablet-shell
 * web user has a visible route back to the desktop site. The iOS shell has no
 * desktop site. A full reload is required because the shell swaps most route
 * tree providers, not just a component subtree.
 */
export function switchToDesktopSite(): void {
    persistShellPreference("off");
    window.location.reload();
}

// Resolution order: iframe guard > `?shell=` URL param > stored localStorage
// preference > auto-detect. iOS is always on. Other platforms require an iPad
// Safari user agent, coarse pointer, and wide viewport. This pure function is
// shared between the subscribed provider and the one-shot fallback.
function resolveTabletShell(params: {
    urlOverride: ShellPreference | null;
    storedPreference: ShellPreference | null;
    isCoarsePointer: boolean;
    isWideEnough: boolean;
    isIos: boolean;
}): boolean {
    // Never render the tablet shell inside an iframe (e.g. Procore/ACC embed
    // panels) — same-origin localStorage can carry a stored preference from
    // an unrelated top-level visit, and the embed routes aren't part of the
    // tablet shell's allowed surface.
    if (isInIframe()) {
        return false;
    }

    const preference = params.urlOverride ?? params.storedPreference;
    if (preference) {
        return preference === "on";
    }

    // The iOS app has no desktop site to fall through to. Keep the shell on
    // independent of screen size or pointer type.
    if (params.isIos) {
        return true;
    }

    return isIPadUserAgent() && params.isCoarsePointer && params.isWideEnough;
}

const TabletShellContext = createContext<boolean | null>(null);

/**
 * Computes the tablet-shell boolean ONCE for the whole app instead of at
 * every one of useTabletShell's call sites: the two matchMedia subscriptions
 * (kept live for rotation changes) and the URL param/localStorage read
 * (evaluated once per mount, not on every render) live here.
 */
export function TabletShellProvider({ children }: { children: ReactNode }) {
    const isCoarsePointer = useMediaQuery(COARSE_POINTER_QUERY);
    const isWideEnough = useMediaQuery(MIN_TABLET_WIDTH_QUERY);
    const platform = usePlatform();

    const urlOverride = useMemo(() => parseUrlShellParam(), []);
    const storedPreference = useMemo(() => readStoredShellPreference(), []);

    useEffect(() => {
        if (urlOverride) {
            persistShellPreference(urlOverride);
        }
    }, [urlOverride]);

    const value = useMemo(
        () =>
            resolveTabletShell({
                urlOverride,
                storedPreference,
                isCoarsePointer,
                isWideEnough,
                isIos: platform.kind === "ios",
            }),
        [urlOverride, storedPreference, isCoarsePointer, isWideEnough, platform.kind],
    );

    // No JSX here — this is a .ts file, not .tsx.
    return createElement(TabletShellContext.Provider, { value }, children);
}

// Mirrors useMediaQuery's own guard (@mui/system/useMediaQuery): jsdom doesn't
// implement matchMedia unless a test mocks it, and most of useTabletShell's
// ~30 call sites' tests don't — default to no match rather than throwing.
function readMatchMedia(query: string): boolean {
    if (typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia(query).matches;
}

// One-shot (non-subscribing) fallback for when no TabletShellProvider is
// mounted above the caller — e.g. a component test that renders a consumer
// standalone. Always called (never conditionally) to satisfy the rules of
// hooks; cheap regardless, since the URL/localStorage read happens once per
// mount via useMemo rather than on every render, and reading matchMedia
// directly (instead of the subscribing useMediaQuery hook) adds no listener.
function useTabletShellFallback(): boolean {
    const platform = usePlatform();
    const urlOverride = useMemo(() => parseUrlShellParam(), []);

    useEffect(() => {
        if (urlOverride) {
            persistShellPreference(urlOverride);
        }
    }, [urlOverride]);

    return useMemo(() => tabletShellAtStartup(platform), [platform]);
}

/**
 * The same one-shot decision outside React: the page's entry uses it to start the chunks the
 * first render will need before App has loaded (`index.tsx`).
 */
export function tabletShellAtStartup(platform: Pick<Platform, "kind">): boolean {
    return resolveTabletShell({
        urlOverride: parseUrlShellParam(),
        storedPreference: readStoredShellPreference(),
        isCoarsePointer: readMatchMedia(COARSE_POINTER_QUERY),
        isWideEnough: readMatchMedia(MIN_TABLET_WIDTH_QUERY),
        isIos: platform.kind === "ios",
    });
}

/**
 * Whether the tablet shell should render instead of normal desktop app chrome.
 *
 * It is always false in an iframe, regardless of URL or stored preference.
 * Otherwise, `?shell=tablet|desktop` wins, then stored preference, then
 * auto-detect. iOS always uses the shell. Other platforms require an iPad
 * Safari user agent, a coarse pointer, and at least 744px width. This avoids
 * a false tablet-shell result on touchscreen desktop or laptop hardware.
 *
 * The hook reads TabletShellProvider when available. Otherwise it computes the
 * result once for the mounted component.
 */
export default function useTabletShell(): boolean {
    const context = useContext(TabletShellContext);
    const fallback = useTabletShellFallback();
    return context ?? fallback;
}
